import { useCallback, useEffect, useRef, useState } from "react";
import type { PlanEvent } from "../../../../shared/types";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import {
  pxToTime,
  pxToSnappedPx,
} from "../../utils/time-positioning";
import { setHoverPosition } from "../../stores/hover-position-store";

interface UseDayColumnInteractionProps {
  readonly date: string;
  readonly isDraggingOrResizing: boolean;
  readonly allDayEvents: readonly PlanEvent[];
  readonly timedEvents: readonly PlanEvent[];
  readonly onSlotCreationMouseDown?: (
    date: string,
    mouseY: number,
    columnEl: HTMLDivElement,
  ) => void;
  readonly onSlotClick?: (startTime: string, endTime: string, anchor: FormAnchor) => void;
}

interface InteractionState {
  readonly hoverY: number | null;
  readonly ctrlHeld: boolean;
  readonly shiftHeld: boolean;
}

export function useDayColumnInteraction({
  date,
  isDraggingOrResizing,
  allDayEvents,
  timedEvents,
  onSlotCreationMouseDown,
  onSlotClick,
}: UseDayColumnInteractionProps): InteractionState & {
  readonly isTimeOccupied: (time: string) => boolean;
  readonly handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  readonly handleMouseLeave: () => void;
  readonly handleGridMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  readonly handleGridTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  readonly handleGridClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  readonly hoverTime: string | null;
  readonly slotOccupied: boolean;
  readonly touchY: number | null;
} {
  const [hoverY, setHoverY] = useState<number | null>(null);
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const [shiftHeld, setShiftHeld] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      setCtrlHeld(e.ctrlKey || e.metaKey);
      setShiftHeld(e.shiftKey);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  const isTimeOccupied = useCallback(
    (time: string): boolean => {
      if (allDayEvents.length > 0) return true;
      return timedEvents.some(
        (e) => time >= e.startTime && time < e.endTime,
      );
    },
    [allDayEvents, timedEvents],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDraggingOrResizing) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const snapped = pxToSnappedPx(y, e.shiftKey);
      setHoverY(snapped);
      setHoverPosition({ date, time: pxToTime(snapped, e.shiftKey) });
    },
    [date, isDraggingOrResizing],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverY(null);
    setHoverPosition(null);
  }, []);

  const rawHoverTime = hoverY !== null ? pxToTime(hoverY, shiftHeld) : null;
  const slotOccupied = rawHoverTime !== null && isTimeOccupied(rawHoverTime);
  const hoverTime = rawHoverTime !== null && (!slotOccupied || ctrlHeld || shiftHeld)
    ? rawHoverTime
    : null;

  const handleGridMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if (isDraggingOrResizing) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const snappedY = pxToSnappedPx(y, e.shiftKey);
      const startTime = pxToTime(snappedY, e.shiftKey);
      if (isTimeOccupied(startTime) && !ctrlHeld && !e.shiftKey) return;
      if (onSlotCreationMouseDown) {
        onSlotCreationMouseDown(date, y, e.currentTarget);
      }
    },
    [date, isDraggingOrResizing, isTimeOccupied, ctrlHeld, onSlotCreationMouseDown],
  );

  // Mobile: track touch position for visual "+" indicator
  const [touchY, setTouchY] = useState<number | null>(null);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
    };
  }, []);

  const handleGridTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (isDraggingOrResizing) return;
      const touch = e.touches[0];
      if (!touch) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const y = touch.clientY - rect.top;
      const snappedY = pxToSnappedPx(y);
      const startTime = pxToTime(snappedY);
      if (isTimeOccupied(startTime) && !ctrlHeld) return;

      // Show "+" indicator briefly
      setTouchY(snappedY);
      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(() => setTouchY(null), 1500);
    },
    [isDraggingOrResizing, isTimeOccupied, ctrlHeld],
  );

  const handleGridClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDraggingOrResizing) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const snappedY = pxToSnappedPx(y, e.shiftKey);
      const startTime = pxToTime(snappedY, e.shiftKey);
      if (isTimeOccupied(startTime) && !ctrlHeld && !e.shiftKey) return;
      const [h, m] = startTime.split(":").map(Number);
      const endMinutes = (h ?? 0) * 60 + (m ?? 0) + 60;
      const endH = Math.floor(endMinutes / 60);
      const endM = endMinutes % 60;
      const endTime = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
      onSlotClick?.(startTime, endTime, {
        colRight: rect.right,
        colLeft: rect.left,
        slotTop: rect.top + snappedY,
      });
    },
    [isDraggingOrResizing, isTimeOccupied, ctrlHeld, onSlotClick],
  );

  return {
    hoverY,
    ctrlHeld,
    isTimeOccupied,
    handleMouseMove,
    handleMouseLeave,
    handleGridMouseDown,
    handleGridTouchStart,
    handleGridClick,
    hoverTime,
    shiftHeld,
    slotOccupied,
    touchY,
  };
}
